import {
  DEFAULT_TIMEZONE,
  type ParticipationValue,
  type PickLanguageValue,
} from '@yoyoclub/shared';
import {
  BookOpen,
  Calendar,
  Clock,
  FlaskConical,
  Heart,
  Leaf,
  LifeBuoy,
  MapPin,
  Music,
  Palette,
  Share2,
  Smile,
  Tag,
  ThumbsUp,
  User,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { listSeparator, localize } from '../../i18n/script';
import { useSiteActions } from '../site/siteActionsContext';
import { iconBtn } from '../ui/redesign';

import PicksMark from './PicksMark';

/** A Pick as public lists return it (backend PublicPickCard). */
export interface PickCardData {
  id: string;
  slug: string;
  type: 'PROVIDER' | 'EVENT';
  titleCN: string | null;
  titleEN: string | null;
  providerName: string;
  category: string;
  cityCN: string | null;
  cityEN: string | null;
  /** Legacy single city, read only while a row has no pair yet (deploy window). */
  city?: string | null;
  region: string[];
  ageMin: number;
  ageMax: number;
  participation: ParticipationValue;
  language: PickLanguageValue;
  priceNoteCN: string | null;
  priceNoteEN: string | null;
  scheduleNoteCN: string | null;
  scheduleNoteEN: string | null;
  whyPickedCN: string | null;
  whyPickedEN: string | null;
  recommendCount: number;
  displayOccurrence: { startTime: string; endTime: string } | null;
  occurrenceCount: number;
  /** Set when a parent suggested it (P9); name null = shown as "a parent". */
  recommendedBy?: { name: string | null } | null;
}

/** Category tint and icon on the card's thumb (prototype CAT_TINT). */
const CATEGORY_LOOK: Record<string, { bg: string; fg: string; Icon: LucideIcon }> = {
  ART: { bg: '#FFEADB', fg: '#A44A12', Icon: Palette },
  NATURE: { bg: '#E6F2E7', fg: '#2A5A32', Icon: Leaf },
  SPORTS: { bg: '#E2F4FC', fg: '#1C6E95', Icon: LifeBuoy },
  STEM: { bg: '#EEE9F7', fg: '#5B4A8A', Icon: FlaskConical },
  COOKING: { bg: '#FFF5D1', fg: '#7A5D00', Icon: UtensilsCrossed },
  MUSIC: { bg: '#E2F4FC', fg: '#1C6E95', Icon: Music },
  DANCE: { bg: '#E2F4FC', fg: '#1C6E95', Icon: Music },
  OTHER: { bg: '#F0E8E1', fg: '#5B4232', Icon: BookOpen },
};
const PARTICIPATION_ICON = { PARENT_CHILD: Users, KID_ONLY: Smile, ADULT_ONLY: User } as const;

/** 40px tinted square with the category icon; Picks never show photos (PRD §5.3). */
export function CategoryThumb({ category }: { category: string }) {
  const { t } = useTranslation('common');
  const look = CATEGORY_LOOK[category] ?? CATEGORY_LOOK.OTHER;
  return (
    <span
      className="grid h-10 w-10 flex-none place-items-center rounded-[11px]"
      style={{ background: look.bg, color: look.fg }}
      title={t(`category.${category}`)}
    >
      <look.Icon className="h-5 w-5" aria-hidden="true" />
    </span>
  );
}

/**
 * YOYO Picks card (prototype `.pk`): no photo, a dashed paper slip, narrower than the
 * activity card. The title link covers the card; share and save sit above it.
 */
export default function PickCard({
  pick,
  className = '',
}: {
  pick: PickCardData;
  className?: string;
}) {
  const { t, i18n } = useTranslation(['picks', 'common']);
  const { isFavorite, toggleFavorite, share } = useSiteActions();
  const locale = i18n.language;
  const href = `/picks/${pick.slug}`;
  const title = localize(pick.titleCN, pick.titleEN, locale);
  const saved = isFavorite('pick', pick.id);
  const PartIcon = PARTICIPATION_ICON[pick.participation];

  const when = (() => {
    if (pick.type === 'PROVIDER') return localize(pick.scheduleNoteCN, pick.scheduleNoteEN, locale);
    if (!pick.displayOccurrence) return '';
    const start = new Date(pick.displayOccurrence.startTime);
    const date = start.toLocaleDateString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: DEFAULT_TIMEZONE,
    });
    const time = start.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: DEFAULT_TIMEZONE,
    });
    const more =
      pick.occurrenceCount > 1 ? t('picks:card.occurrences', { count: pick.occurrenceCount }) : '';
    return `${date} ${time}${more}`;
  })();

  const ageText =
    pick.ageMin >= 18
      ? t('common:card.adults')
      : pick.ageMax >= 99
        ? t('common:card.agePlus', { min: pick.ageMin })
        : t('common:card.ageRange', { min: pick.ageMin, max: pick.ageMax });
  const audience = [
    ageText,
    t(`common:participation.${pick.participation}`),
    pick.language !== 'NA' ? t(`common:teachingLanguage.${pick.language}`) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const place = [
    localize(pick.cityCN, pick.cityEN, locale) || pick.city,
    pick.region.map((r) => t(`common:region.${r}`)).join(listSeparator(locale)),
  ]
    .filter(Boolean)
    .join(' · ');
  const price = localize(pick.priceNoteCN, pick.priceNoteEN, locale);
  const why = localize(pick.whyPickedCN, pick.whyPickedEN, locale);

  const row = 'flex min-w-0 items-center gap-[7px]';
  const icon = 'h-[13px] w-[13px] flex-none text-mute';

  return (
    <article
      className={`group relative flex flex-col gap-[7px] rounded-2xl border-[1.5px] border-dashed border-pick-line bg-pick-bg px-[13px] pb-2 pt-[13px] text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-[3px] hover:border-primary hover:bg-white motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <CategoryThumb category={pick.category} />
        <div>
          <PicksMark small />
          <span className="mt-px block font-ui text-[11.5px] text-mute">
            {pick.type === 'EVENT' ? t('picks:card.event') : t('picks:card.provider')} ·{' '}
            {t(`common:category.${pick.category}`)}
          </span>
        </div>
      </div>
      <h3 className="line-clamp-2 font-ui text-[15px] font-semibold leading-[1.35] text-ink">
        <Link
          to={href}
          className="after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:rounded-2xl focus-visible:after:ring-[3px] focus-visible:after:ring-primary"
        >
          {title}
        </Link>
      </h3>
      <div className="-mt-1 truncate text-[12.5px] text-mute">
        {[
          t('picks:card.from', { provider: pick.providerName }),
          pick.recommendedBy
            ? pick.recommendedBy.name
              ? t('picks:detail.recommendedBy', { name: pick.recommendedBy.name })
              : t('picks:detail.recommendedByParent')
            : '',
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
      <div className="flex flex-col gap-0.5 font-text text-[12.5px] text-ink-2">
        {when && (
          <div className={row}>
            {pick.type === 'EVENT' ? (
              <Calendar className={icon} aria-hidden="true" />
            ) : (
              <Clock className={icon} aria-hidden="true" />
            )}
            <span className="min-w-0 truncate">{when}</span>
          </div>
        )}
        {place && (
          <div className={row}>
            <MapPin className={icon} aria-hidden="true" />
            <span className="min-w-0 truncate">{place}</span>
          </div>
        )}
        <div className={row}>
          <PartIcon className={icon} aria-hidden="true" />
          <span className="min-w-0 truncate">{audience}</span>
        </div>
        {price && (
          <div className={row}>
            <Tag className={icon} aria-hidden="true" />
            <span className="min-w-0 truncate">{price}</span>
          </div>
        )}
      </div>
      {why && (
        <p className="rounded-[10px] bg-white px-[9px] py-[7px] text-[12.5px] leading-[1.5] text-ink-2">
          {/* Clamp inside the padding so a third line cannot peek through it */}
          <span className="line-clamp-2">{why}</span>
        </p>
      )}
      <div className="mt-auto flex items-center">
        <span className="mr-auto inline-flex items-center gap-[5px] text-[12.5px] font-medium text-primary-dark">
          <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
          {t('picks:card.recommends', { count: pick.recommendCount })}
        </span>
        <button
          type="button"
          className={`${iconBtn} relative z-10 !min-h-[34px] !min-w-[34px]`}
          aria-label={t('common:share.button')}
          onClick={() => share(href, title)}
        >
          <Share2 className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`${iconBtn} relative z-10 !min-h-[34px] !min-w-[34px] ${saved ? 'text-heart' : ''}`}
          aria-label={saved ? t('common:favorite.remove') : t('common:favorite.add')}
          aria-pressed={saved}
          onClick={() => toggleFavorite('pick', pick.id)}
        >
          <Heart className={`h-4 w-4 ${saved ? 'fill-current' : ''}`} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}
